import { useCallback } from "react";

import { AdvancedPersonWithEReputationStatus } from "src/core/constants/folder";
import { useFolder } from "src/core/contexts/FolderContext";
import { useCallEReputationMutation } from "src/core/gql/graphql";
import { useStakeholderDuplicatesActions } from "src/core/hooks/folder/stakeholder-duplicates/useStakeholderDuplicatesActions";

export type StakeholderDuplicatesReferenceProfile =
  | { type: "current" }
  | { type: "candidate"; candidate: AdvancedPersonWithEReputationStatus }
  | null;

export type StakeholderDuplicatesPayload = {
  stakeholderId: string;
  selectedDuplicateCandidates: AdvancedPersonWithEReputationStatus[];
  referenceProfile: StakeholderDuplicatesReferenceProfile;
};

const getSelectedDuplicateCandidateIds = (
  selectedDuplicateCandidates: AdvancedPersonWithEReputationStatus[]
): string[] => {
  return selectedDuplicateCandidates
    .map((candidate) => candidate.id)
    .filter((id): id is string => Boolean(id));
};

/**
 * Définit le stakeholder courant comme référence et remplace les autres stakeholders candidats
 * sélectionnés par celui-ci.
 */
const replaceEntitiesWithCurrentStakeholder = async ({
  stakeholderId,
  selectedDuplicateCandidateIds,
  replaceEntity,
}: {
  stakeholderId: string;
  selectedDuplicateCandidateIds: string[];
  replaceEntity: (params: { idToDelete: string; replacementId: string }) => Promise<void>;
}): Promise<string> => {
  await Promise.all(
    selectedDuplicateCandidateIds.map((idToDelete) =>
      replaceEntity({
        idToDelete,
        replacementId: stakeholderId,
      })
    )
  );

  return stakeholderId;
};

/**
 * Définit le stakeholder candidat sélectionné comme référence et remplace le stakeholder courant
 * ainsi que les autres stakeholders candidats par celui-ci.
 */
const replaceEntitiesWithCandidate = async ({
  stakeholderId,
  replacementId,
  selectedDuplicateCandidateIds,
  replaceEntity,
}: {
  stakeholderId: string;
  replacementId: string;
  selectedDuplicateCandidateIds: string[];
  replaceEntity: (params: { idToDelete: string; replacementId: string }) => Promise<void>;
}): Promise<string> => {
  const idsToDelete = Array.from(
    new Set([
      stakeholderId,
      ...selectedDuplicateCandidateIds.filter((candidateId) => candidateId !== replacementId),
    ])
  );

  await Promise.all(
    idsToDelete.map((idToDelete) =>
      replaceEntity({
        idToDelete,
        replacementId,
      })
    )
  );

  return replacementId;
};

/*
 * Hook dédié à la résolution des doublons de stakeholders.
 */
export const useResolveStakeholderDuplicates = () => {
  const { folderFile, refetch: refetchFolder } = useFolder();
  const { replaceEntity, updateDuplicateCheckDone } = useStakeholderDuplicatesActions();
  const [callEReputation] = useCallEReputationMutation();

  const resolveStakeholderDuplicates = useCallback(
    async (payload: StakeholderDuplicatesPayload): Promise<void> => {
      const { stakeholderId, selectedDuplicateCandidates, referenceProfile } = payload;

      const selectedDuplicateCandidateIds = getSelectedDuplicateCandidateIds(
        selectedDuplicateCandidates
      );

      let personId: string | undefined;

      if (referenceProfile?.type === "current") {
        personId = await replaceEntitiesWithCurrentStakeholder({
          stakeholderId,
          selectedDuplicateCandidateIds,
          replaceEntity,
        });
      }

      if (referenceProfile?.type === "candidate" && referenceProfile.candidate.id) {
        personId = await replaceEntitiesWithCandidate({
          stakeholderId,
          replacementId: referenceProfile.candidate.id,
          selectedDuplicateCandidateIds,
          replaceEntity,
        });
      }

      if (personId) {
        if (folderFile.folderFile?.id) {
          await callEReputation({
            variables: {
              folderFileId: folderFile.folderFile.id,
            },
            context: {
              suppressRedirect: true,
            },
          });
        }
        await updateDuplicateCheckDone({
          personId,
        });
      }

      await refetchFolder();
    },
    [
      updateDuplicateCheckDone,
      refetchFolder,
      replaceEntity,
      folderFile.folderFile?.id,
      callEReputation,
    ]
  );

  return {
    resolveStakeholderDuplicates,
  };
};
